import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { handleInsertTemplate } from '.';
import LoadingOverlay from 'src/components/common/LoadingOverlay';

interface EmailTemplate {
  id?: string;
  templateName: string;
  subject: string;
  body: string;
  footer: string;
  attachments: File[];
}

// Enum for template status
enum TemplateStatus {
  InReview = 'In Review',
  Approved = 'Approved',
  Withdraw = 'Withdrawn',
  Rejected = 'Rejected',
}

interface EmailTemplateConfigProps {
  template?: EmailTemplate | null;
  onCancel: () => void;
}

const CreateTemplate: React.FC<EmailTemplateConfigProps> = ({
  template,
  onCancel
}) => {
  // State variables
  const [templateName, setTemplateName] = useState<TemplateStatus>(
    (template?.templateName as TemplateStatus) || TemplateStatus.Approved
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [subject, setSubject] = useState(template?.subject || '');
  const [body, setBody] = useState(template?.body || '');
  const [footer, setFooter] = useState(template?.footer || '');
  const [attachments, setAttachments] = useState<File[]>(
    template?.attachments || []
  );

  // Refs for TextFields to insert variables at cursor position
  const subjectRef = useRef<HTMLInputElement>(null);
  const footerRef = useRef<HTMLInputElement>(null);
  const quillRef = useRef<ReactQuill>(null);

  // Reference to track the currently focused field
  const [focusedField, setFocusedField] = useState<
    'subject' | 'body' | 'footer' | null
  >(null);

  // List of dynamic variables
  const dynamicVariables = [
    '<%= TownshipName %>',
    '<%= ApplicationStatus %>',
    '<%= ApplicationNumber %>',
    '<%= PermitName %>',
    '<%= Date %>',
    '<%= PlatformName %>',
    '<%= Fee %>',
    '<%= FirstName %>',
    '<%= LastName %>',
    '<%= RejectionReason %>'
  ];

  // Function to decode HTML entities
  const decodeHTMLEntities = (text: string) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  // Function to sanitize the template body before saving
  const sanitizeTemplateBody = (htmlContent: string) => {
    // First decode any existing HTML entities
    let decodedContent = decodeHTMLEntities(htmlContent);

    // Replace any remaining HTML-encoded template variables with their proper form
    decodedContent = decodedContent.replace(/&lt;%=/g, '<%=');
    decodedContent = decodedContent.replace(/%&gt;/g, '%>');

    return decodedContent;
  };

  // Function to insert variable into the currently focused field
  const handleInsertVariable = (variable: string) => {
    if (focusedField === 'subject' && subjectRef.current) {
      const input = subjectRef.current;
      const startPos = input.selectionStart || 0;
      const endPos = input.selectionEnd || 0;
      const newValue =
        subject.substring(0, startPos) +
        variable +
        subject.substring(endPos, subject.length);
      setSubject(newValue);

      // Set the cursor position after the inserted variable
      setTimeout(() => {
        input.selectionStart = input.selectionEnd = startPos + variable.length;
        input.focus();
      }, 0);
    } else if (focusedField === 'body' && quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      if (range) {
        quill.insertText(range.index, variable);
        quill.setSelection(range.index + variable.length, 0);
      } else {
        quill.insertText(quill.getLength(), variable);
      }
    } else if (focusedField === 'footer' && footerRef.current) {
      const input = footerRef.current;
      const startPos = input.selectionStart || 0;
      const endPos = input.selectionEnd || 0;
      const newValue =
        footer.substring(0, startPos) +
        variable +
        footer.substring(endPos, footer.length);
      setFooter(newValue);

      // Set the cursor position after the inserted variable
      setTimeout(() => {
        input.selectionStart = input.selectionEnd = startPos + variable.length;
        input.focus();
      }, 0);
    } else {
      alert('Please focus on a field to insert the variable.');
    }
  };

  // Handle file attachment changes
  const handleAttachmentChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files) {
      setAttachments(Array.from(event.target.files));
    }
  };

  // Save the template
  const handleSave = async () => {
    const sanitizedBody = sanitizeTemplateBody(body);
    const newTemplate: EmailTemplate = {
      id: template?.id || undefined,
      templateName,
      subject,
      body: sanitizedBody,
      footer,
      attachments
    };
    await handleInsertTemplate(setLoading, newTemplate, template?.id);
    onCancel();
  };

  // Cancel and discard changes
  const handleCancel = () => {
    onCancel();
  };

  // Effect to update state when template changes (useful when editing multiple templates)
  useEffect(() => {
    setTemplateName(
      (template?.templateName as TemplateStatus) || TemplateStatus.Approved
    );
    setSubject(template?.subject || '');
    setBody(template?.body || '');
    setFooter(template?.footer || '');
    setAttachments(template?.attachments || []);
  }, [template]);

  return (
    <Box p={{xs:0, sm:3}}>
      {loading && <LoadingOverlay />}

      <Grid container spacing={2}>
        {/* Left side: Form fields */}
        <Grid item xs={12} md={8.5}>
          <Grid container spacing={2}>
            {/* Template Name Select Field */}
            <Grid item xs={12} mt={2}>
              <FormControl fullWidth>
                <InputLabel>Template Name</InputLabel>
                <Select
                  value={templateName}
                  label="Template Name"
                  onChange={(e) =>
                    setTemplateName(e.target.value as TemplateStatus)
                  }
                >
                  {Object.values(TemplateStatus).map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Subject Field */}
            <Grid item xs={12}>
              <TextField
                label="Subject"
                fullWidth
                value={subject}
                inputRef={(input) => {
                  if (input !== null) {
                    subjectRef.current = input;
                  }
                }}
                onFocus={() => setFocusedField('subject')}
                onChange={(e) => setSubject(e.target.value)}
              />
            </Grid>

            {/* Body Field with Rich Text Editor */}
            <Grid item xs={12}>
              <Typography variant="subtitle1">Body:</Typography>
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={body}
                onChange={setBody}
                onFocus={() => setFocusedField('body')}
                onBlur={() => setFocusedField(null)}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['link', 'image'],
                    ['clean']
                  ]
                }}
                formats={[
                  'header',
                  'bold',
                  'italic',
                  'underline',
                  'strike',
                  'list',
                  'bullet',
                  'link',
                  'image'
                ]}
                style={{ height: '300px', marginBottom: '50px' }}
              />
            </Grid>

            {/* Footer Field */}
            <Grid item xs={12} mt={{xs:2, sm:0}}>
              <TextField
                label="Footer"
                fullWidth
                value={footer}
                inputRef={(input) => {
                  if (input !== null) {
                    footerRef.current = input;
                  }
                }}
                onFocus={() => setFocusedField('footer')}
                onBlur={() => setFocusedField(null)}
                onChange={(e) => setFooter(e.target.value)}
              />
            </Grid>

            {/* Save and Cancel Buttons */}
            <Grid container mt={2} ml={2}>
              {/* Attachment Button */}
              <Grid item xs={12} sm={6} mb={{xs:2, sm:"auto"}}>
                <Button variant="outlined" component="label" sx={{width : {xs: "100%", sm:"auto"}}}>
                  Attach Documents
                  <input
                    type="file"
                    hidden
                    multiple
                    onChange={handleAttachmentChange}
                  />
                </Button>
                {attachments.map((file, index) => (
                  <Typography ml={1} mt={1} color={'primary'} key={index}>
                    {file.name}
                  </Typography>
                ))}
              </Grid>

              <Grid
                item
                xs={6}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'end'
                }}
              >
                <Button
                  sx={{ marginRight: 1 }}
                  variant="outlined"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Right side: Dynamic Variables */}
        <Grid item xs={12} md={3.5}>
          <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
            Available Dynamic Variables:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexWrap: 'wrap',
              overflowY: 'auto'
            }}
          >
            {dynamicVariables.map((variable, index) => (
              <Button
                key={index}
                variant="outlined"
                size="small"
                sx={{ margin: 0.5 }}
                onClick={() => handleInsertVariable(variable)}
              >
                {variable}
              </Button>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CreateTemplate;
